interface SeedBounds {
  min: number;
  max: number;
}

export const DEFAULT_SEED_MIN = 0;
export const DEFAULT_SEED_MAX = 2147483647;

export function toSeedBound(value: unknown, fallback: number): number {
  if (typeof value !== 'number' && typeof value !== 'string') return fallback;
  if (typeof value === 'string' && value.trim() === '') return fallback;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

export function normalizeSeedBounds(min: unknown, max: unknown): SeedBounds {
  const integerMin = Math.ceil(toSeedBound(min, DEFAULT_SEED_MIN));
  const integerMax = Math.floor(toSeedBound(max, DEFAULT_SEED_MAX));

  return integerMin <= integerMax
    ? { min: integerMin, max: integerMax }
    : { min: integerMax, max: integerMin };
}

/**
 * Offset a seed within an inclusive range, wrapping at either boundary.
 */
export function offsetSeed(base: number, index: number, min: unknown, max: unknown): number {
  const bounds = normalizeSeedBounds(min, max);
  const rangeSize = bounds.max - bounds.min + 1;

  if (rangeSize <= 1) {
    return bounds.min;
  }

  const safeBase = Number.isFinite(base) ? Math.trunc(base) : bounds.min;
  const safeIndex = Number.isFinite(index) ? Math.trunc(index) : 0;
  const relativeSeed = safeBase - bounds.min + safeIndex;

  return ((relativeSeed % rangeSize) + rangeSize) % rangeSize + bounds.min;
}

/**
 * Clamp a seed to an inclusive integer range.
 */
export function clampSeed(seed: number, min: unknown, max: unknown): number {
  const bounds = normalizeSeedBounds(min, max);
  const safeSeed = Number.isFinite(seed) ? Math.trunc(seed) : bounds.min;

  return Math.max(bounds.min, Math.min(bounds.max, safeSeed));
}

/**
 * Return a random integer within an inclusive range.
 */
export function nextRandomSeed(min: unknown, max: unknown): number {
  const bounds = normalizeSeedBounds(min, max);
  const rangeSize = bounds.max - bounds.min + 1;

  if (rangeSize <= 1) {
    return bounds.min;
  }

  return Math.floor(Math.random() * rangeSize) + bounds.min;
}
